import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { Tv, Play, BookOpen, Youtube, Globe, CheckCircle, Lightbulb } from 'lucide-react';

const HowToWatch = () => {
  return (
    <div className="min-h-screen" data-testid="how-to-watch-page">
      <PageHeader 
        title="Cómo Ver la NFL"
        subtitle="Todo lo que necesitas para disfrutar del fútbol americano"
        backgroundImage="https://images.unsplash.com/photo-1565789426992-9c335aa0a62c?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Where to Watch */}
        <section>
          <SectionHeader 
            title="¿Dónde Ver Partidos?"
            quarter="1Q"
            icon={Tv}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed">
              La NFL está disponible en múltiples plataformas según tu país. Aquí te explicamos las 
              <strong className="text-white"> opciones más comunes</strong> para hispanohablantes.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "TV por Cable/Satélite",
                  options: ["ESPN Deportes", "Fox Sports", "NFL Network"],
                  note: "Partidos selectos con comentarios en español",
                  color: "#3b82f6"
                },
                {
                  title: "Streaming",
                  options: ["NFL Game Pass", "DAZN", "Amazon Prime Video"],
                  note: "Todos los partidos en vivo y on-demand",
                  color: "#22c55e"
                },
                {
                  title: "Redes Abiertas",
                  options: ["CBS", "Fox", "NBC", "ABC/ESPN"],
                  note: "Partidos selectos (EE.UU.)",
                  color: "#f97316"
                },
                {
                  title: "Apps Oficiales",
                  options: ["NFL App", "Yahoo Sports", "NFL+"],
                  note: "Highlights, stats y algunos partidos en móvil",
                  color: "#8b5cf6"
                }
              ].map((platform, index) => (
                <div key={index} className="tactical-card p-6 rounded-r-xl" style={{ borderLeftColor: platform.color }}>
                  <h4 
                    className="text-lg font-bold text-white mb-3 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {platform.title}
                  </h4>
                  <ul className="space-y-2 mb-3">
                    {platform.options.map((option, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        {option}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500 text-sm">{platform.note}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-emerald-900/20 border border-emerald-800 p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <Lightbulb className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 
                    className="text-lg font-bold text-emerald-400 mb-2"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Consejo Pro
                  </h4>
                  <p className="text-slate-300 text-sm">
                    <strong className="text-white">NFL Game Pass International</strong> es la mejor opción para ver 
                    todos los partidos fuera de EE.UU. Incluye partidos en vivo, repeticiones, NFL RedZone y 
                    contenido exclusivo. Los precios varían según la temporada.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tips for Beginners */}
        <section>
          <SectionHeader 
            title="Consejos para Principiantes"
            quarter="2Q"
            icon={BookOpen}
          />
          
          <div className="space-y-6">
            {[
              {
                number: "01",
                title: "Empieza con resúmenes",
                description: "Los highlights de 10-15 minutos te dan las mejores jugadas sin invertir 3+ horas. NFL en español tiene resúmenes de cada partido."
              },
              {
                number: "02",
                title: "Busca comentarios en español",
                description: "Al principio es más fácil seguir el juego si entiendes los comentarios. ESPN Deportes y otras cadenas ofrecen narración en español."
              },
              {
                number: "03",
                title: "Ten esta guía a mano",
                description: "Mientras ves el partido, consulta el glosario cuando escuches un término nuevo. Con el tiempo los memorizarás."
              },
              {
                number: "04",
                title: "Sigue a un equipo",
                description: "Elige un equipo y síguelo toda la temporada. Conocer a los jugadores y su estilo hace todo más interesante."
              },
              {
                number: "05",
                title: "No te frustres",
                description: "El fútbol americano tiene muchas reglas y situaciones. Es normal no entender todo al principio. ¡Disfruta el proceso!"
              }
            ].map((tip, index) => (
              <div key={index} className="flex gap-6 group">
                <span 
                  className="text-5xl font-black text-slate-800 group-hover:text-emerald-500/30 transition-colors"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {tip.number}
                </span>
                <div>
                  <h4 
                    className="text-xl font-bold text-white mb-2"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {tip.title}
                  </h4>
                  <p className="text-slate-400">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recommended Content */}
        <section>
          <SectionHeader 
            title="Contenido Recomendado"
            quarter="3Q"
            icon={Play}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed">
              Estos recursos te ayudarán a aprender más sobre la NFL y disfrutar del contenido 
              <strong className="text-white"> entre partidos</strong>.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="info-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <Youtube className="w-5 h-5 text-red-400" />
                  </div>
                  <h4 
                    className="text-lg font-bold text-white uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    YouTube
                  </h4>
                </div>
                <ul className="space-y-3 text-sm">
                  <li className="text-slate-400">
                    <strong className="text-white">NFL en español</strong> - Canal oficial con highlights y análisis
                  </li>
                  <li className="text-slate-400">
                    <strong className="text-white">The Ringer</strong> - Análisis táctico profundo (inglés)
                  </li>
                  <li className="text-slate-400">
                    <strong className="text-white">NFL Films</strong> - Documentales y contenido histórico
                  </li>
                </ul>
              </div>
              
              <div className="info-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h4 
                    className="text-lg font-bold text-white uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    Sitios Web
                  </h4>
                </div>
                <ul className="space-y-3 text-sm">
                  <li className="text-slate-400">
                    <strong className="text-white">NFL.com/es</strong> - Noticias, stats y schedules oficiales
                  </li>
                  <li className="text-slate-400">
                    <strong className="text-white">ESPN Deportes</strong> - Cobertura completa en español
                  </li>
                  <li className="text-slate-400">
                    <strong className="text-white">Pro Football Reference</strong> - Base de datos estadística
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="tactical-card p-6 rounded-r-xl">
              <h4 
                className="text-lg font-bold text-white mb-4 uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Podcasts en Español
              </h4>
              <p className="text-slate-400 mb-4">
                Los podcasts son perfectos para mantenerte al día mientras haces otras cosas:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["NFL en español", "Primero y Diez", "Touchdown Latino", "La Zona NFL"].map((podcast, index) => (
                  <div key={index} className="bg-slate-800/50 px-4 py-3 rounded-lg text-center">
                    <span className="text-white text-sm font-medium">{podcast}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Schedule */}
        <section>
          <div className="bg-slate-800/50 p-8 rounded-xl border border-slate-700">
            <h3 
              className="text-2xl font-bold text-white mb-6 uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Horarios de Partidos
            </h3>
            <p className="text-slate-400 mb-6">
              Los partidos de NFL se juegan principalmente los domingos, con algunos jueves y lunes:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  day: "Domingo temprano",
                  time: "19:00 - 20:00 CET",
                  note: "(13:00 - 14:00 hora México)"
                },
                {
                  day: "Domingo tarde",
                  time: "22:00 - 22:30 CET",
                  note: "(16:00 - 16:30 hora México)"
                },
                {
                  day: "Sunday/Monday Night",
                  time: "02:00 - 02:30 CET (+1)",
                  note: "(20:00 - 20:30 hora México)"
                }
              ].map((slot, index) => (
                <div key={index} className="text-center p-4 bg-slate-700/30 rounded-lg">
                  <h4 
                    className="text-lg font-bold text-emerald-400 mb-1"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {slot.day}
                  </h4>
                  <span className="text-white text-xl font-mono">{slot.time}</span>
                  <span className="text-slate-500 text-xs block mt-1">{slot.note}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-6 text-center">
              * Los horarios pueden variar según el país y la semana de temporada
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HowToWatch;
